import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { AdvertisementForm } from './AdvertisementForm';
import { toast } from '../../hooks/use-toast';
import {
  Image, Link as LinkIcon, Type, Building2, Plus, Pencil, Trash2,
  Eye, EyeOff, GripVertical, Upload, X, ExternalLink, MousePointer,
  BarChart3, Loader2, Save, RefreshCw
} from 'lucide-react';

const AdvertisementsManager = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    link_text: 'Learn More',
    company_name: '',
    ad_type: 'sidebar',
    is_active: true,
    start_date: '',
    end_date: ''
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/advertisements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdvertisements(data.advertisements || []);
      }
    } catch (error) {
      console.error('Error fetching advertisements:', error);
      toast({
        title: 'Error',
        description: 'Failed to load advertisements',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_BASE}/api/admin/advertisements/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.image_url }));
        toast({
          title: 'Success',
          description: 'Image uploaded successfully'
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast({
        title: 'Error',
        description: 'Title is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingAd 
        ? `${API_BASE}/api/admin/advertisements/${editingAd.id}`
        : `${API_BASE}/api/admin/advertisements`;
      
      const response = await fetch(url, {
        method: editingAd ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: editingAd ? 'Advertisement updated' : 'Advertisement created'
        });
        resetForm();
        fetchAdvertisements();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save advertisement',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title || '',
      description: ad.description || '',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      link_text: ad.link_text || 'Learn More',
      company_name: ad.company_name || '',
      ad_type: ad.ad_type || 'sidebar',
      is_active: ad.is_active ?? true,
      start_date: ad.start_date || '',
      end_date: ad.end_date || ''
    });
    setShowForm(true);
    
    // Scroll to top immediately
    window.scrollTo(0, 0);
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/advertisements/${adId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: 'Deleted',
          description: 'Advertisement deleted successfully'
        });
        fetchAdvertisements();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete advertisement',
        variant: 'destructive'
      });
    }
  };

  const toggleActive = async (ad) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/advertisements/${ad.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !ad.is_active })
      });
      fetchAdvertisements();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link_url: '',
      link_text: 'Learn More',
      company_name: '',
      ad_type: 'sidebar',
      is_active: true,
      start_date: '',
      end_date: ''
    });
    setEditingAd(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
            <Image className="w-6 h-6 text-red-600" />
            Advertisements Manager
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage banner ads displayed on the homepage sidebar
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAdvertisements} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => { 
            resetForm(); 
            setShowForm(true); 
            window.scrollTo(0, 0);
          }} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-1" /> Add Advertisement
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <AdvertisementForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          editingAd={editingAd}
          fileInputRef={fileInputRef}
          handleImageUpload={handleImageUpload}
          uploading={uploading}
          resetForm={resetForm}
          apiBase={API_BASE}
        />
      )}

      {/* Advertisements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : advertisements.length === 0 ? (
        <Card className="dark:bg-gray-800">
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No advertisements yet</p>
            <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-1" /> Create First Ad
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {advertisements.map((ad) => (
            <Card key={ad.id} className={`dark:bg-gray-800 overflow-hidden ${
              !ad.is_active ? 'opacity-60' : ''
            }`}>
              {ad.image_url && (
                <div className="aspect-video bg-gray-50 dark:bg-gray-700 relative flex items-center justify-center p-2">
                  <img
                    src={ad.image_url.startsWith('/uploads/') ? ad.image_url : (ad.image_url.startsWith('/') ? `${API_BASE}${ad.image_url}` : ad.image_url)}
                    alt={ad.title}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.padding = '16px';
                      e.target.src = '/trainerlist-logo.png';
                    }}
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Badge className={ad.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {ad.is_active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                      {ad.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              )}
              <CardContent className="p-4">
                <h3 className="font-bold text-lg dark:text-white mb-1">{ad.title}</h3>
                {ad.company_name && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    {ad.company_name}
                  </p>
                )}
                {ad.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                    {ad.description}
                  </p>
                )}
                
                {/* Stats */}
                <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {ad.impressions || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <MousePointer className="w-3 h-3" /> {ad.clicks || 0} clicks
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t dark:border-gray-700">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(ad)}
                    className="flex-1"
                  >
                    {ad.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {ad.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(ad)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => handleDelete(ad.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">📌 How Advertisements Work</h4>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• <strong>Sidebar Ads</strong> appear in a fixed position on the right side of the homepage</li>
            <li>• <strong>Banner Ads</strong> appear at the top of the trainer listings</li>
            <li>• Active ads will be displayed to all visitors</li>
            <li>• Track impressions (views) and clicks for each ad</li>
            <li>• If no active ads exist, a "Want to Advertise?" placeholder will be shown</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvertisementsManager;
